import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Container, 
  Divider,
  Pagination,
  Chip,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Footer from '../Materials/Footer';

export default function SearchResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const searchQuery = new URLSearchParams(location.search).get('query') || '';
  const [page, setPage] = React.useState(1);
  const [results, setResults] = React.useState({ 
    entities: [], 
    equipments: [],
    loading: true,
    error: null
  });
  
  const itemsPerPage = 10;

  // Funções para o header
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setOpenDrawer(!openDrawer);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setOpenDrawer(false);
  };

  const handleExit = () => {
    localStorage.removeItem('user');
    navigate('/');
  };

  React.useEffect(() => {
  const fetchResults = async () => {
    try {
      setResults(prev => ({...prev, loading: true}));
      
      const entitiesResponse = await fetch('https://api.hub4food.colab4food.com/api/entities');
      const allEntities = await entitiesResponse.json();
      
      const equipmentsResponse = await fetch('https://api.hub4food.colab4food.com/api/equipments');
      const allEquipments = await equipmentsResponse.json();
      
      // Filtrar entidades
      const filteredEntities = allEntities.filter(entity =>
        entity.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      
      // Filtrar equipamentos e enriquecer com nome da entidade
      const filteredEquipments = allEquipments
        .filter(equipment =>
          equipment.equipment_name.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .map(equipment => {
          // Encontrar a entidade correspondente
          const entity = allEntities.find(entity => entity.id === equipment.entity_id);
          return {
            ...equipment,
            entity_name: entity ? entity.name : 'Unknown Entity'
          };
        });
      
      setResults({
        entities: filteredEntities,
        equipments: filteredEquipments,
        loading: false,
        error: null
      });
    } catch (error) {
      setResults({
        entities: [],
        equipments: [],
        loading: false,
        error: error.message
      });
    }
  };

  if (searchQuery) {
    fetchResults();
  }
}, [searchQuery]);

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const paginate = (items) => {
    const startIndex = (page - 1) * itemsPerPage;
    return items.slice(startIndex, startIndex + itemsPerPage);
  };

  const totalResults = results.entities.length + results.equipments.length;

  const resultCardStyle = {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    p: 3,
    mb: 3,
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: 3,
      cursor: 'pointer'
    }
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: '#3FA5D4' }}>
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            sx={{ mr: 2 }}
            onClick={handleDrawerToggle}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Hub4Food<sup>®</sup>
          </Typography>
          <div>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={() => handleNavigation('/usersettings')}>Settings</MenuItem>
              <MenuItem onClick={handleExit}>Exit</MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>

      {/* Drawer Menu */}
      <Drawer
        anchor="left"
        open={openDrawer}
        onClose={handleDrawerToggle}
      >
        <Box sx={{ width: 240, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ padding: 2, textAlign: 'center' }}>
            <img src="logocolab.png" alt="Hub4Food Logo" style={{ width: '80%', maxWidth: '120px' }} />
          </Box>
          <Divider sx={{ marginTop: 1 }} />
          <List>
            <ListItem button onClick={() => handleNavigation('/mainpage')}>
              <ListItemText primary="Home" />
            </ListItem>
            <ListItem button onClick={() => handleNavigation('/entities')}>
              <ListItemText primary="Entities" />
            </ListItem>
            <ListItem button onClick={() => handleNavigation('/ideationPlatform')}>
              <ListItemText primary="Ideation Platform" />
            </ListItem>
            <ListItem button onClick={() => handleNavigation('/contacts')}>
              <ListItemText primary="Contacts" />
            </ListItem>
            <ListItem button onClick={() => handleNavigation('/listentities')}>
              <ListItemText primary="Actions" />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Conteúdo da página */}
      <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
          Showing results for "{searchQuery}"
        </Typography>

        {results.loading ? (
          <Typography>Loading results...</Typography>
        ) : results.error ? (
          <Typography color="error">Error: {results.error}</Typography>
        ) : totalResults === 0 ? (
          <Typography>No results found for "{searchQuery}"</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {/* Seção de Entidades */}
            {results.entities.length > 0 && (
              <Box>
                <Typography variant="h5" gutterBottom sx={{ 
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}>
                  Entities
                  <Chip 
                    label={results.entities.length} 
                    color="primary" 
                    size="small"
                  />
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {paginate(results.entities).map((entity) => (
                    <Box 
                      key={entity.id}
                      sx={resultCardStyle}
                      onClick={() => navigate(`/detailedentities/${entity.id}`)}
                    >
                      <Typography variant="h6" sx={{ 
                        mb: 1,
                        color: 'primary.main',
                        fontWeight: 'bold'
                      }}>
                        {entity.name}
                      </Typography>
                      {entity.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {entity.description}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
                
                {results.entities.length > itemsPerPage && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination
                      count={Math.ceil(results.entities.length / itemsPerPage)}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      size="large"
                    />
                  </Box>
                )}
              </Box>
            )}

            {/* Seção de Equipamentos */}
{results.equipments.length > 0 && (
  <Box>
    <Typography variant="h5" gutterBottom sx={{ 
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      gap: 2
    }}>
      Equipments
      <Chip 
        label={results.equipments.length} 
        color="secondary" 
        size="small"
      />
    </Typography>
    <Divider sx={{ mb: 3 }} />
    
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {paginate(results.equipments).map((equipment) => (
        <Box 
          key={equipment.id}
          sx={resultCardStyle}
          onClick={() => navigate(`/detailedEquipment/${equipment.id}`)}
        >
          <Typography variant="h6" sx={{ 
            mb: 1,
            color: 'primary.main',
            fontWeight: 'bold'
          }}>
            {equipment.equipment_name}
          </Typography>
          {equipment.description && (
            <Typography variant="body2" color="text.secondary" sx={{ 
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}>
              {equipment.description}
            </Typography>
          )}
          <Typography variant="caption" sx={{
            display: 'block',
            mt: 1,
            color: 'text.secondary',
            fontStyle: 'italic'
          }}>
            Entity: {equipment.entity_name}
          </Typography>
        </Box>
      ))}
    </Box>
    
    {results.equipments.length > itemsPerPage && (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Pagination
          count={Math.ceil(results.equipments.length / itemsPerPage)}
          page={page}
          onChange={handlePageChange}
          color="primary"
          size="large"
        />
      </Box>
    )}
  </Box>
)}
          </Box>
        )}
      </Container>
      
      <Footer />
    </Box>
  );
}